import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { SAVE_INDICATOR_LABEL } from '../lib/save-indicator';
import { FEE_TOTAL_UNRESOLVED_LABEL, presentFeeCount, presentFeeTotal } from '../lib/fee-display';
import { useEFiling } from '../contexts/EFilingContext';

interface SummaryV2Props {
  brand: BrandConfig;
}

const SummaryV2: React.FC<SummaryV2Props> = ({ brand }) => {
  const { wizardState, orderId, createDraftOrder, saveDraftToServer, saveDraft, saveIndicator } =
    useEFiling();
  const { orderInfo, caseInfo, courtFees } = wizardState;

  const countyDisplay = orderInfo.county
    ? orderInfo.county.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';

  const filingTypeLabel =
    orderInfo.filingType === 'case-initiation'
      ? 'New case'
      : orderInfo.filingType === 'subsequent-filing'
        ? 'Existing case'
        : '';

  // SPEC-SOR-343 (R7/R12): the rail's fees section renders the resolved record
  // through the shared presenters — the record's total and its non-zero
  // portions when the quote resolved, the state label when it did not. A
  // non-resolved quote shows no figure, ever: the accumulator a draft may
  // still carry is the charge seam's input, not a display source.
  const feeTotal = presentFeeTotal(courtFees);
  const feeCount = presentFeeCount(courtFees);

  const handleSaveDraft = async () => {
    try {
      if (!orderId) {
        await createDraftOrder();
      } else {
        await saveDraftToServer();
      }
      saveDraft();
    } catch {
      // SOR-355: the provider recorded the outcome and the rail below renders
      // it. Swallowing here keeps a failed manual save from becoming an
      // unhandled rejection.
    }
  };

  const labelStyle: React.CSSProperties = {
    color: brand.colors.textLight,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const sectionStyle: React.CSSProperties = {
    borderBottom: `1px solid ${brand.colors.border}`,
    paddingBottom: '12px',
    marginBottom: '12px',
  };

  return (
    <div
      className="rounded-lg border p-4 sticky top-6"
      style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
      data-testid="summary-v2"
    >
      <h3
        className="text-sm font-bold mb-4 pb-3 border-b"
        style={{ color: brand.colors.text, borderColor: brand.colors.border }}
      >
        Filing Summary
      </h3>

      {countyDisplay && (
        <div style={sectionStyle}>
          <p style={labelStyle}>Court</p>
          <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
            {countyDisplay} County
          </p>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `${brand.colors.success || '#22C55E'}1A`,
              color: brand.colors.success || '#22C55E',
            }}
            data-testid="accepts-efilings-badge"
          >
            Accepts e-filings
          </span>
        </div>
      )}

      {(caseInfo.caseTitle || caseInfo.caseCategoryName || caseInfo.caseType?.code) && (
        <div style={sectionStyle}>
          <p style={labelStyle}>Case Info</p>
          {caseInfo.caseTitle && (
            <p className="text-sm font-medium truncate" style={{ color: brand.colors.text }} title={caseInfo.caseTitle} data-testid="summary-case-title">
              {caseInfo.caseTitle}
            </p>
          )}
          {caseInfo.caseCategoryName && (
            <p className="text-xs mt-1" style={{ color: brand.colors.textLight }} data-testid="summary-case-category">
              {caseInfo.caseCategoryName}
            </p>
          )}
          {/* SOR-194: the code is part of the guard, not decoration. The name
              alone is a claim about what the filing's type IS, and a name with
              no code is exactly the reading the court did not accept — this
              sidebar stated `Type: Unlawful Detainer` while the Upload step's
              picker said we couldn't read it. The routing rule means a fresh
              extraction can no longer produce that pair; this holds the line for
              a draft that reaches the sidebar before hydration backfills it. */}
          {caseInfo.caseType?.code && caseInfo.caseType.name && (
            <p className="text-xs mt-1" style={{ color: brand.colors.textLight }} data-testid="summary-case-type">
              {caseInfo.caseType.name}
            </p>
          )}
        </div>
      )}

      {filingTypeLabel && (
        <div style={sectionStyle}>
          <p style={labelStyle}>Filing Type</p>
          <p className="text-sm font-medium" style={{ color: brand.colors.text }} data-testid="summary-filing-type">
            {filingTypeLabel}
          </p>
          {orderInfo.filingType === 'subsequent-filing' && caseInfo.trialCourtCaseNumber && (
            <p className="text-xs mt-1" style={{ color: brand.colors.textLight }} data-testid="summary-case-number">
              Case #{caseInfo.trialCourtCaseNumber}
            </p>
          )}
        </div>
      )}

      <div style={sectionStyle}>
        <p style={labelStyle}>Estimated Fees</p>
        <p className="text-lg font-bold" style={{ color: brand.colors.text }} data-testid="summary-fee-total">
          {feeTotal ? feeTotal.text : FEE_TOTAL_UNRESOLVED_LABEL}
        </p>
        {feeCount !== null && feeCount > 0 && (
          <p className="text-xs mt-0.5" style={{ color: brand.colors.textLight }} data-testid="summary-fee-count">
            {feeCount} fee{feeCount !== 1 ? 's' : ''} included
          </p>
        )}
      </div>

      <button
        onClick={handleSaveDraft}
        className="w-full px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:bg-gray-50"
        style={{ borderColor: brand.colors.primary, color: brand.colors.link }}
        data-testid="summary-save-draft"
      >
        Save Draft
      </button>
      {/* SOR-355: the rail renders the ONE shared indicator state — text while
          saved/failed, nothing while unsaved. No local timer, no local copy of
          the save message; the text comes from the same catalog the breadcrumb
          badge reads. The rail's absence while unsaved is a genuine distinction
          (the sidebar has no room for a permanent "Not saved") and stays. */}
      {saveIndicator !== 'unsaved' && (
        <p
          className="text-xs text-center mt-1"
          style={{ color: brand.colors.textLight }}
          data-testid="summary-save-state"
        >
          {SAVE_INDICATOR_LABEL[saveIndicator]}
        </p>
      )}

      <div
        className="mt-4 p-3 rounded-lg border"
        style={{ borderColor: brand.colors.primary, backgroundColor: `${brand.colors.primary}08` }}
        data-testid="first-time-cta"
      >
        <p className="text-sm font-semibold mb-1" style={{ color: brand.colors.text }}>
          First time filing?
        </p>
        <p className="text-xs" style={{ color: brand.colors.textLight }}>
          Check out our step-by-step guide to learn what you need to complete your first e-filing.
        </p>
      </div>
    </div>
  );
};

export default SummaryV2;
